import { useCallback } from 'react';
import { ChatConversation } from './types';
import { useChatConversationList } from './useChatConversationList';
import { useChatConversationActions } from './useChatConversationActions';
import { useGlobalMessageNotification } from './useGlobalMessageNotification';
import { useChatFavorites } from './useChatFavorites';
import { conversationIdentityMatch } from '../lib/conversationScope';

export function useChatConversations(
    listConnectionId: string | null,
    actionConnectionId?: string | null,
) {
    const {
        conversations,
        setConversations,
        isLoadingConversations,
        userId,
        // 🆕 Scroll Infinito de Conversas
        isLoadingMore,
        hasMoreConversations,
        loadMoreConversations,
        backendCounters,
    } = useChatConversationList(listConnectionId);

    // Hook para notificação global de novas mensagens via PartyKit (realtime)
    useGlobalMessageNotification();

    // Hook de favoritos para auto-desfavoritar ao transferir (se member)
    const { checkAndAutoUnfavorite } = useChatFavorites();

    const {
        startNewConversation,
        assignConversation,
        transferConversation,
        removeAssignment,
        closeConversation,
        reopenConversation
    } = useChatConversationActions(
        userId,
        actionConnectionId ?? listConnectionId,
        setConversations,
        checkAndAutoUnfavorite,
        conversations,
    );

    const updateConversation = useCallback((
        conversationId: string,
        updates: Partial<ChatConversation>,
        connectionId?: string | null,
    ) => {
        const scopeId = connectionId ?? actionConnectionId ?? listConnectionId;
        setConversations((prev: ChatConversation[]) => prev.map((c) => (
            conversationIdentityMatch(c, conversationId, scopeId) ? { ...c, ...updates } : c
        )));
    }, [setConversations, actionConnectionId, listConnectionId]);

    return {
        conversations,
        setConversations,
        isLoadingConversations,
        startNewConversation,
        assignConversation,
        transferConversation,
        removeAssignment,
        closeConversation,
        reopenConversation,
        updateConversation,
        currentUserId: userId,
        // 🆕 Scroll Infinito de Conversas
        isLoadingMoreConversations: isLoadingMore,
        hasMoreConversations,
        loadMoreConversations,
        backendCounters,
    };
}
